import fetch from 'node-fetch'
import { z } from 'zod'

import { MeasurLocale } from './src/locale/i18n'
import {
  AccreditationStatus,
  IndustryOrMaterial,
  ProductScopeType,
  QualitySystem,
  TypeOfTesting,
} from './src/logic/catalog'
import publicConfig from './src/logic/public-config'

const apiToken = process.env.MEASUR_API_TOKEN || ''

const headers = { 'User-Agent': 'measur-website build', 'auth-token': apiToken }

const LocalisedData = z.record(z.nativeEnum(MeasurLocale), z.string())
type LocalisedData = z.infer<typeof LocalisedData>

export const MultiCurrency = z.object({
  EUR: z.number(),
  GBP: z.number(),
  USD: z.number(),
})
export type MultiCurrency = z.infer<typeof MultiCurrency>

const APIProductOption = z.object({
  displayName: LocalisedData,
  id: z.string().uuid(),
  priceDeltaCents: MultiCurrency,
})
export type APIProductOption = z.infer<typeof APIProductOption>

const APIProductParameter = z.object({
  helperText: LocalisedData.nullable(),
  id: z.string().uuid(),
  options: z.array(APIProductOption),
  priceScope: ProductScopeType,
  slug: z.string(),
  title: LocalisedData,
})
export type APIProductParameter = z.infer<typeof APIProductParameter>

const APIProductTatOption = APIProductOption.extend({
  turnaroundWeeks: z.object({
    max: z.number().nullable(),
    min: z.number().nullable(),
  }),
})
type APIProductTatOption = z.infer<typeof APIProductTatOption>
const APIProductTatParameter = APIProductParameter.omit({
  options: true,
}).extend({
  options: z.array(APIProductTatOption),
})
export type APIProductTatParameter = z.infer<typeof APIProductTatParameter>

// Currency in ISO 4217
export const Currency = z.enum(['EUR', 'GBP', 'USD'])
export type Currency = z.infer<typeof Currency>

const APIProduct = z.object({
  accreditation: AccreditationStatus,
  contactFormInstructions: z.record(
    z.nativeEnum(MeasurLocale),
    z.string().nullable(),
  ),
  description: LocalisedData,
  descriptionRaw: LocalisedData,
  detectionLimit: LocalisedData,
  deviceTypes: z.array(z.string()),
  employeeId: z.string(),
  estimatedPrice: z
    .object({
      internalNote: z.string().trim(),
      maxCents: MultiCurrency.nullable(),
      minCents: MultiCurrency,
    })
    .nullable(),
  fixedPrice: z
    .object({
      firstSampleCents: MultiCurrency,
      nextSamplesCents: MultiCurrency.nullable(),
    })
    .nullable(),
  id: z.string(),
  industryOrMaterial: z.array(IndustryOrMaterial),
  metaDescription: LocalisedData,
  metaTitle: LocalisedData,
  name: LocalisedData,
  parameters: z.array(APIProductParameter),
  pipedriveProductId: z.number().nullable(),
  popularityRank: z.number(),
  qualitySystems: z.array(QualitySystem),
  sampleRequirements: LocalisedData,
  slug: LocalisedData,
  standards: z.array(z.string()),
  suitableSampleMaterials: LocalisedData,
  tatParameter: APIProductTatParameter.nullable(),
  turnaroundWeeks: z.object({
    max: z.number().nullable(),
    min: z.number().nullable(),
  }),
  typeOfTesting: z.array(TypeOfTesting),
  webshopEnabled: z.boolean(),
  websitePricingModel: z.enum(['estimated', 'fixed']),
})
export type APIProduct = z.infer<typeof APIProduct>

const APIDeviceType = z.object({
  deviceTypeId: z.string(),
  nameLong: LocalisedData.nullable(),
  nameShort: LocalisedData.nullable(),
  parentDeviceTypeId: z.string().nullable(),
})
export type APIDeviceType = z.infer<typeof APIDeviceType>

export const fetchProducts = async () => {
  if (!publicConfig.apiHost) {
    throw new Error('process.env.GATSBY_MEASUR_API_HOST not provided')
  }
  const res = await fetch(
    `${publicConfig.apiHost}/v1/products/search?limit=0`,
    {
      body: JSON.stringify({ visibleOnWebsite: true }),
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      method: 'POST',
    },
  )

  const products = await res.json().then(
    (result: {
      data: APIProduct[]
      meta: {
        currentPage: number
        totalItems: number
        totalPages: number
      }
    }) => result.data,
  )

  return products.map(product =>
    APIProduct.parse({
      ...product,
      // convert rich texts to raw string as graphql doesn't support nested objects
      contactFormInstructions: Object.fromEntries(
        Object.entries(product.contactFormInstructions).map(([k, v]) => [
          k,
          v ? JSON.stringify(v) : null,
        ]),
      ) as LocalisedData,
      description: Object.fromEntries(
        Object.entries(product.description).map(([k, v]) => [
          k,
          JSON.stringify(v),
        ]),
      ) as LocalisedData,
    }),
  )
}

const APIEmployee = z.object({
  customerFacingPhone: z.string(),
  googleAccount: z.string(),
  id: z.string(),
  isActive: z.boolean(),
  name: z.object({
    full: z.string(),
    short: z.string(),
  }),
})
export type APIEmployee = z.infer<typeof APIEmployee>

export const fetchEmployees = async () => {
  if (!publicConfig.apiHost) {
    throw new Error('process.env.GATSBY_MEASUR_API_HOST not provided')
  }
  const res = await fetch(`${publicConfig.apiHost}/v1/employees`, { headers })
  return z.array(APIEmployee).parse(await res.json())
}

const APIStandard = z.object({
  code: z.string(),
  group: z.string(),
  id: z.string(),
})
export type APIStandard = z.infer<typeof APIStandard>
const APIStandardGroup = z.object({
  id: z.string(),
  name: z.string(),
  standards: z.array(APIStandard),
})
export type APIStandardGroup = z.infer<typeof APIStandardGroup>

export const fetchStandardGroups = async () => {
  if (!publicConfig.apiHost) {
    throw new Error('process.env.GATSBY_MEASUR_API_HOST not provided')
  }
  const res = await fetch(`${publicConfig.apiHost}/v1/standard-groups`, {
    headers,
  })
  return z.array(APIStandardGroup).parse(await res.json())
}

export const fetchDeviceTypes = async () => {
  if (!publicConfig.apiHost) {
    throw new Error('process.env.GATSBY_MEASUR_API_HOST not provided')
  }
  const res = await fetch(`${publicConfig.apiHost}/v1/device-types`, {
    headers,
  })
  return z.array(APIDeviceType).parse(await res.json())
}
